import { Component, inject, model, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ButtonDirective } from '../button/button.directive';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { ModalComponent } from '../modal/modal.component';
import { ConfigService } from '../../services/config.service';
import { DictionaryService } from '../../services/dictionary.service';

/** Date format pattern: DD-MM-YYYY */
const DATE_PATTERN = /^(\d{2})-(\d{2})-(\d{4})$/;

@Component({
  selector: 'app-time-range-modal',
  templateUrl: './time-range-modal.component.html',
  styleUrls: ['./time-range-modal.component.scss'],
  imports: [ButtonDirective, DatePickerComponent, ModalComponent, ReactiveFormsModule],
})
export class TimeRangeModalComponent {
  protected readonly dictionaryService = inject(DictionaryService);
  private readonly configService = inject(ConfigService);

  // Inputs & Outputs
  readonly isOpen = model<boolean>(false);
  readonly onClose = output<void>();

  // Form
  protected readonly rangeForm = new FormGroup({
    from: new FormControl<string>(this.formatDate(this.configService.from()), {
      nonNullable: true,
      validators: [Validators.required],
    }),
    to: new FormControl<string>(this.formatDate(this.configService.to()), {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  /** Current 'from' value for 'to' date picker min constraint */
  get fromValue(): string {
    return this.rangeForm.controls.from.value;
  }

  /** Current 'to' value for 'from' date picker max constraint */
  get toValue(): string {
    return this.rangeForm.controls.to.value;
  }

  close(): void {
    this.onClose.emit();
  }

  apply(): void {
    if (this.rangeForm.invalid) return;

    const { from, to } = this.rangeForm.value;
    const fromDate = this.parseDate(from!);
    const toDate = this.parseDate(to!);

    if (fromDate && toDate) {
      this.configService.setDate({ from: fromDate, to: toDate });
      window.location.reload();
    }

    this.close();
  }



  /**
   * Formats a Date object to DD-MM-YYYY string
   */
  private formatDate(date: Date | undefined): string {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /**
   * Parses a DD-MM-YYYY string to a Date object
   */
  private parseDate(dateStr: string): Date | null {
    const match = dateStr.match(DATE_PATTERN);
    if (!match) return null;

    const [, day, month, year] = match.map(Number);
    return new Date(year, month - 1, day);
  }
}
